import { Box, styled, TextField, Select } from "@mui/material";

export const StyledOrdersContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$embedded" && prop !== "background",
})(({ theme, background, $embedded }) => ({
  background: background
    ? theme.palette.custom.orderCards
    : theme.palette.custom.white,
  paddingTop: "20px",
  ...($embedded && {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  }),
}));

/** Dashboard embedded only — layout/scroll/footer hide; OrderTable unchanged */
export const StyledEmbeddedTableScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: "320px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "& .MuiPaper-root": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: 0,
    boxShadow: "none",
  },
  "& .MuiTableContainer-root": {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  "& .MuiTableHead-root .MuiTableCell-head": {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.custom.primary,
  },
  "& .MuiPaper-root > :last-child": {
    display: "none",
  },
}));

export const StyledEmbeddedTableChrome = styled(Box)(() => ({
  flexShrink: 0,
}));

/** Cap panel growth so the dashboard page itself does not gain extra scroll */
const dashboardPanelCap = "calc(100vh - 260px)";

const dashboardPanelAt = (heightPx) => ({
  minHeight: heightPx,
  maxHeight: `min(${heightPx}, ${dashboardPanelCap})`,
});

/** Match Virtual Locker card height on dashboard — table scrolls inside; capped to viewport */
const dashboardPanelHeight = (theme) => ({
  ...dashboardPanelAt("600px"),
  overflow: "hidden",
  [theme.breakpoints.only("xl")]: {
    ...dashboardPanelAt("500px"),
  },
  "@media screen and (min-width: 1393px) and (max-width:1418px)": {
    ...dashboardPanelAt("512px"),
  },
  "@media screen and (min-width: 1200px) and (max-width:1392px)": {
    ...dashboardPanelAt("540px"),
  },
  "@media screen and (min-width: 1082px) and (max-width:1199px)": {
    ...dashboardPanelAt("520px"),
  },
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    maxHeight: "none",
    overflow: "visible",
  },
});

export const StyledTableLayout = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$embedded",
})(({ theme, $embedded }) => ({
  background: theme.palette.custom.white,
  padding: $embedded ? theme.padding.size.medium : theme.padding.size.large,
  boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.08)",
  borderRadius: theme.borderRadius.radius.regular,
  width: "100%",
  boxSizing: "border-box",
  ...($embedded && {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    border: theme.borders.highlighted,
    ...dashboardPanelHeight(theme),
  }),
}));

export const StyledBoxStyle = styled(Box)(({ theme }) => ({
  paddingTop: "20px",
  width: "100%",
  display: "flex",
  gap: 20,
  flexWrap: "wrap",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}));

export const StyledCustomerSearch = styled(TextField)(({ theme }) => ({
  height: "36px",
  flex: 1,
  minWidth: 0,
  width: "100%",
  "& .MuiInputBase-input": {
    fontSize: theme.typography.small.fontSize,
  },
  "& .MuiOutlinedInput-root": {
    height: "36px",
    paddingLeft: "20px",
    borderRadius: theme.borderRadius.radius.medium,
    background: theme.palette.custom.searchBg,
    color: theme.palette.custom.orderButtonColor,
  },
  "& .MuiOutlinedInput-input": {
    padding: "0px",
    background: "transparent",
  },
  "& fieldset": { border: "none" },
}));

export const StyledStatusFilter = styled(Select)(({ theme }) => ({
  height: "36px",
  minWidth: "160px",
  flexShrink: 0,
  fontSize: theme.typography.small.fontSize,
  borderRadius: theme.borderRadius.radius.medium,
  background: theme.palette.custom.searchBg,
  color: theme.palette.custom.orderButtonColor,
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    padding: "0px 32px 0px 20px",
    display: "flex",
    alignItems: "center",
    height: "36px",
    boxSizing: "border-box",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minWidth: "unset",
  },
}));
